import React, { useState } from 'react';
import { dateTimeConversion } from '../../utils/javascript';
import { SearchContainer } from '../../components/TeamPermission/styles';
import { Icon, Button, Input, Tooltip, Popover, Table, Badge, message } from 'antd';
import { FailureReasonText, StatusTag } from './dataTableStyles';
import { Tags } from './batchFiltersStyles';
import { withTranslation } from 'react-i18next';
import { BATCHLIST_DATA_TABLE } from './translationValues';

const NAMESPACES = ['BATCHLIST_DATA_TABLE'];

const DataTable = props => {
  const {
    tagValues,
    tableData,
    dataCount,
    printFailures,
    selectedRowKeys,
    setSelectedRowKeys,
    setSelectedAll,
    selectedPages,
    setSelectedPages,
    updateBatchDetails,
    loading,
    inQueueCount,
    t
  } = props;

  const tagColVal = (tagId, tagName) => {
    let colVal = {
      title: tagName,
      dataIndex: tagId,
      ellipsis: true,
      render: (text, record) => {
        const tagVal = record.tagInfo?.map((tag, key) => {
          if (tag[tagId]) {
            return (
              <Tags tableTags={true} key={key}>
                {tag[tagId]}
              </Tags>
            );
          }
        });
        return tagVal;
      },
      ...getColumnSearchProps('tagInfo'),
    };
    return colVal;
  };

  const onSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    hideDefaultSelections: true,
    onSelectAll: (selected, selectedRows, changeRows) => {
      setSelectedAll(selected);
      if (!selected) {
        setSelectedRowKeys([]);
        setSelectedPages(0);
      }
    },
    hideSelectAll: inQueueCount > 0,
    onSelect: (selected, selectedRows, changeRows) => {
      let pageCount = selectedRows
        ? selectedPages + selected.docPages
        : selectedPages - selected.docPages;
      setSelectedPages(pageCount);
    },
    getCheckboxProps: record => ({
      disabled: record.printStatus === 'InQueue',
    }),
  };

  const tagsColumn = () => {
    let allTagsCol = [];
    let dummyData = tagValues?.map(tagVal => {
      allTagsCol.push(tagColVal(tagVal.tagId, tagVal.tagName));
    });
    return allTagsCol;
  };

  const getInputPlaceholder = (dataIndex, t) => {
    switch(dataIndex) {
      case 'docName':
        return t(BATCHLIST_DATA_TABLE.DOCUMENT_NAME_PLACEHOLDER.key, BATCHLIST_DATA_TABLE.DOCUMENT_NAME_PLACEHOLDER.value);
        break;
      case 'tagInfo':
        return t(BATCHLIST_DATA_TABLE.TAG_PLACEHOLDER.key, BATCHLIST_DATA_TABLE.TAG_PLACEHOLDER.value);
        break;
      case 'batchName':
        return t(BATCHLIST_DATA_TABLE.BATCH_NAME_PLACEHOLDER.key, BATCHLIST_DATA_TABLE.BATCH_NAME_PLACEHOLDER.value);
        break;
      case 'docPages':
        return t(BATCHLIST_DATA_TABLE.PAGES_PLACEHOLDER.key, BATCHLIST_DATA_TABLE.PAGES_PLACEHOLDER.value);
        break;
      default: return '';
    }
  }

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <SearchContainer style={{ padding: 8 }} mailRoom={true}>
        <Input
          className="searchInput"
          placeholder={`Search by ${getInputPlaceholder(dataIndex, t)}`}
          value={selectedKeys}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => {
            if(dataIndex === 'docPages') {
              if(isNaN(selectedKeys) || !(selectedKeys >= 1 && selectedKeys <= 100)) {
                message.error(t(BATCHLIST_DATA_TABLE.RANGE_LIMIT_ERROR.key, BATCHLIST_DATA_TABLE.RANGE_LIMIT_ERROR.value))
              } else {
                confirm();
              }
            } else {
              confirm();
            }
          }}
          icon="search"
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          {t(BATCHLIST_DATA_TABLE.SEARCH_BUTTON.key, BATCHLIST_DATA_TABLE.SEARCH_BUTTON.value)}
        </Button>
        <Button
          onClick={() => {
            if (dataIndex === 'docName') {
            } else if (dataIndex === 'tagInfo') {
            }
            clearFilters();
            confirm();
          }}
          size="small"
          style={{ width: 90 }}
        >
          {t(BATCHLIST_DATA_TABLE.CLEAR_BUTTON.key, BATCHLIST_DATA_TABLE.CLEAR_BUTTON.value)}
        </Button>
      </SearchContainer>
    ),
    filterIcon: filtered => (
      <Icon type="search" style={{ color: filtered ? '#007480' : '#f2f2f2' }} />
    ),
  });

  const tableColumns = [
    {
      title: t(BATCHLIST_DATA_TABLE.DOCUMENT_COLUMN_NAME.key, BATCHLIST_DATA_TABLE.DOCUMENT_COLUMN_NAME.value),
      dataIndex: 'docName',
      ...getColumnSearchProps('docName'),
      render: (text, record) => {
        return (
          <Tooltip placement="topLeft" title={text?.length > 25 ? text : ''}>
            <a href={record.storageLink} target="blank">
              {text}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: t(BATCHLIST_DATA_TABLE.BATCH_COLUMN_NAME.key, BATCHLIST_DATA_TABLE.BATCH_COLUMN_NAME.value),
      dataIndex: 'batchName',
      ...getColumnSearchProps('batchName'),
    },
    {
      title: t(BATCHLIST_DATA_TABLE.PAGE_COLUMN_NAME.key, BATCHLIST_DATA_TABLE.PAGE_COLUMN_NAME.value),
      dataIndex: 'docPages',
      ...getColumnSearchProps('docPages'),
    },
    ...tagsColumn(),
    {
      title: printFailures ? t(BATCHLIST_DATA_TABLE.FAILED_ON_COLUMN_NAME.key, BATCHLIST_DATA_TABLE.FAILED_ON_COLUMN_NAME.value) : t(BATCHLIST_DATA_TABLE.PRINT_COLUMN_NAME.key, BATCHLIST_DATA_TABLE.PRINT_COLUMN_NAME.value),
      dataIndex: 'updatedOn',
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      render: value => {
        return dateTimeConversion(value, true, true, false);
      },
    },
    {
      title: t(BATCHLIST_DATA_TABLE.FAILURE_REASON_COLUMN_NAME.key, BATCHLIST_DATA_TABLE.FAILURE_REASON_COLUMN_NAME.value),
      dataIndex: 'reason',
      render: (text, record) => {
        return (
          text?.length > 0 && (
            <Popover
              placement="leftTop"
              content={<FailureReasonText>{text}</FailureReasonText>}
              trigger="click"
            >
              <Icon type="question-circle" />
            </Popover>
          )
        );
      },
    },
    {
      title: t(BATCHLIST_DATA_TABLE.STATUS_COLUMN_NAME.key, BATCHLIST_DATA_TABLE.STATUS_COLUMN_NAME.value),
      dataIndex: 'printStatus',
      filters: [
        { text: t(BATCHLIST_DATA_TABLE.PRINT_DROPDOWN_OPTION.key, BATCHLIST_DATA_TABLE.PRINT_DROPDOWN_OPTION.value), value: 'Printed' },
        { text: t(BATCHLIST_DATA_TABLE.QUEUE_DROPDOWN_OPTION.key, BATCHLIST_DATA_TABLE.QUEUE_DROPDOWN_OPTION.value), value: 'InQueue' },
        { text: t(BATCHLIST_DATA_TABLE.FAILED_DROPDOWN_OPTION.key, BATCHLIST_DATA_TABLE.FAILED_DROPDOWN_OPTION.value), value: 'Failed' },
      ],
      render: (text, record) => {
        let color = text?.toLowerCase().includes('print')
          ? '#5cd65c'
          : text?.toLowerCase().includes('fail')
            ? '#ff3333'
            : text?.toLowerCase().includes('queue')
              ? '#33adff'
              : '';
        let bgColor = text?.toLowerCase().includes('print')
          ? '#d6f5d6'
          : text?.toLowerCase().includes('fail')
            ? '#ffcccc'
            : text?.toLowerCase().includes('queue')
              ? '#ccebff'
              : '';
        return (
          <StatusTag color={color} backgroundColor={bgColor}>
            <Badge color={color} status={text?.toLowerCase().includes('queue') && 'processing'} />{' '}
            {text}
          </StatusTag>
        );
      },
    },
  ];

  const filteredColumns = printFailures
    ? tableColumns.filter(column => column.dataIndex !== 'printStatus')
    : tableColumns.filter(column => column.dataIndex !== 'batchName');

  return (
    <Table
      rowSelection={rowSelection}
      rowKey={record => record.id}
      columns={filteredColumns}
      dataSource={tableData}
      pagination={{
        hideOnSinglePage: true,
        total: dataCount,
        defaultPageSize: 8,
      }}
      onChange={(pagination, filters, sorter) => updateBatchDetails(pagination, filters, sorter)}
      loading={loading}
    />
  );
};

export default withTranslation(NAMESPACES)(DataTable);
